import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Image, Video, Loader2, X, ChevronLeft, ChevronRight,
  Heart, Download, LogOut, Users, Menu, Camera, CheckSquare, Check
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Masonry from 'react-masonry-css';

// Custom Wedding Rings Icon (two gold bands)
const WeddingRingsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Left ring */}
    <ellipse cx="11" cy="12" rx="8" ry="9" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
    {/* Right ring */}
    <ellipse cx="21" cy="12" rx="8" ry="9" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
  </svg>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientGalleryView = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [gallery, setGallery] = useState(null);
  const [content, setContent] = useState({ photos: [], videos: [], groups: [], favorites: [] });
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Multi-selection state (sposi only)
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [zipDownloading, setZipDownloading] = useState(false);

  const toggleSelect = (photoId) => {
    setSelectedIds(prev => 
      prev.includes(photoId) ? prev.filter(id => id !== photoId) : [...prev, photoId]
    );
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds([]);
  };

  const handleZipDownload = async () => {
    if (selectedIds.length === 0 || zipDownloading) return;
    setZipDownloading(true);
    try {
      const res = await axios.post(`${API}/gallery/download-zip`, 
        { photo_ids: selectedIds },
        { responseType: 'blob' }
      );
      const blobUrl = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${gallery?.title?.replace(/ /g, '_') || 'foto'}_foto.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      exitSelectionMode();
    } catch (error) {
      console.error('ZIP download error:', error);
      alert('Errore durante il download del file ZIP');
    } finally {
      setZipDownloading(false);
    }
  };

  useEffect(() => {
    fetchGalleryData();
  }, []);

  const fetchGalleryData = async () => {
    try {
      // Get gallery info for this client
      const res = await axios.get(`${API}/gallery`);
      setGallery({
        id: res.data.id,
        title: res.data.title,
        event_date: res.data.event_date,
        description: res.data.description,
        can_favorite: res.data.can_favorite
      });
      setContent({
        photos: res.data.photos || [],
        videos: res.data.videos || [],
        groups: res.data.groups || [],
        favorites: res.data.favorites || []
      });
    } catch (error) {
      console.error('Error fetching gallery:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleFavorite = async (photoId) => {
    try {
      const res = await axios.post(`${API}/gallery/favorites/${photoId}`);
      // Update local state
      setContent(prev => ({
        ...prev,
        favorites: res.data.is_favorite 
          ? [...prev.favorites, photoId]
          : prev.favorites.filter(id => id !== photoId)
      }));
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Filter and sort photos by filename
  let filteredPhotos = (selectedGroup === 'all' 
    ? content.photos 
    : content.photos.filter(p => p.group_id === selectedGroup)
  ).sort((a, b) => (a.original_filename || '').localeCompare(b.original_filename || ''));

  // Apply favorites filter
  if (showOnlyFavorites) {
    filteredPhotos = filteredPhotos.filter(p => content.favorites.includes(p.id));
  }

  const favoritesCount = content.favorites.length;

  // Lightbox handlers
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = () => {
    setLightboxIndex((prev) => (prev === 0 ? filteredPhotos.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setLightboxIndex((prev) => (prev === filteredPhotos.length - 1 ? 0 : prev + 1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/30"
            onClick={() => setSidebarOpen(false)}
          />
          {/* Sidebar panel */}
          <div className="absolute left-0 top-0 h-full w-64 bg-white shadow-xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-8">
                <span className="font-medium text-gray-800">Menu</span>
                <button 
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <nav className="space-y-2">
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 bg-gray-100 rounded-lg transition-colors"
                >
                  <Image className="w-5 h-5" />
                  Tutte le foto
                </button>
                <button
                  onClick={() => { navigate('/videos'); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Video className="w-5 h-5" />
                  Tutti i video
                </button>
                <button
                  onClick={() => { navigate('/instawedding'); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Camera className="w-5 h-5" />
                  Instawedding
                </button>
                <button
                  onClick={() => { navigate('/photobooth'); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Camera className="w-5 h-5" />
                  Foto Boot
                </button>
                <button
                  onClick={() => { handleLogout(); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  Esci
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Header row with menu icon, title centered, empty right */}
          <div className="flex items-start justify-between">
            {/* Menu icon - Left, aligned to top */}
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-1"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Title, date and icon centered */}
            <div className="text-center flex-1">
              <h1 className="font-wedding text-4xl text-gray-800">{gallery?.title}</h1>
              {gallery?.event_date && (
                <p className="text-sm text-gray-500 mt-1">
                  {new Date(gallery.event_date).toLocaleDateString('it-IT', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </p>
              )}
              <div className="flex justify-center mt-2">
                {user?.role === 'sposi' ? (
                  <WeddingRingsIcon className="w-8 h-6" />
                ) : (
                  <Users className="w-5 h-5 text-blue-500" />
                )}
              </div>
            </div>
            
            {/* Empty space for balance */}
            <div className="w-9" />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* VIDEO GALLERY - First video type */}
        {content.videos.filter(v => v.video_type === 'video_gallery').map(video => (
          <div key={video.id} className="mb-6">
            <ClientVideoCard video={video} />
          </div>
        ))}

        {/* VIDEO TRAILER - Second video type */}
        {content.videos.filter(v => v.video_type === 'trailer').map(video => (
          <div key={video.id} className="mb-6">
            <ClientVideoCard video={video} />
          </div>
        ))}

        {/* Filters row - Group selector, Video button and Favorites */}
        <div className="flex items-center justify-center gap-4 mb-6 flex-wrap">
          {/* Group Filter */}
          {content.groups.length > 0 && (
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
            >
              <option value="all">Tutti i gruppi foto</option>
              {content.groups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          )}
          
          {/* Video button */}
          <button
            onClick={() => navigate('/videos')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            <Video className="w-4 h-4" />
            Tutti i video
          </button>
          
          {/* Photo Booth button */}
          <button
            onClick={() => navigate('/photobooth')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            <Camera className="w-4 h-4" />
            Foto boot
          </button>
          
          {/* Multi-select toggle (sposi only) */}
          {user?.role === 'sposi' && filteredPhotos.length > 0 && (
            <button
              onClick={() => selectionMode ? exitSelectionMode() : setSelectionMode(true)}
              data-testid="toggle-selection-mode-btn"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                selectionMode 
                  ? 'bg-black text-white border border-black' 
                  : 'border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <CheckSquare className="w-4 h-4" />
              {selectionMode ? 'Annulla selezione' : 'Seleziona'}
            </button>
          )}
          
          {/* Favorites Filter */}
          {gallery?.can_favorite && favoritesCount > 0 && (
            <button
              onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                showOnlyFavorites 
                  ? 'bg-red-100 text-red-700 border border-red-300' 
                  : 'border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Heart className="w-4 h-4 fill-red-500 text-red-500" />
              {favoritesCount}
            </button>
          )}
        </div>

        {/* PHOTOS SECTION - Masonry grid */}
        {filteredPhotos.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Image className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nessuna foto in questo gruppo</p>
          </div>
        ) : (
          <Masonry
            breakpointCols={{
              default: 5,
              1280: 4,
              1024: 3,
              768: 2,
              640: 2
            }}
            className="masonry-grid"
            columnClassName="masonry-grid-column"
          >
            {filteredPhotos.map((photo, index) => (
              <ClientPhotoCard 
                key={photo.id} 
                photo={photo}
                groupName={content.groups.find(g => g.id === photo.group_id)?.name}
                onOpenLightbox={() => openLightbox(index)}
                canFavorite={gallery?.can_favorite}
                isFavorite={content.favorites.includes(photo.id)}
                onToggleFavorite={() => toggleFavorite(photo.id)}
                selectionMode={selectionMode}
                isSelected={selectedIds.includes(photo.id)}
                onToggleSelect={() => toggleSelect(photo.id)}
              />
            ))}
          </Masonry>
        )}
      </div>

      {/* Selection bottom bar */}
      {selectionMode && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-2xl" data-testid="selection-bottom-bar">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
            <span className="text-sm font-medium">
              {selectedIds.length} {selectedIds.length === 1 ? 'foto selezionata' : 'foto selezionate'}
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={exitSelectionMode}
                disabled={zipDownloading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={handleZipDownload}
                disabled={selectedIds.length === 0 || zipDownloading}
                data-testid="download-zip-btn"
                className="flex items-center gap-2 px-5 py-2 bg-black text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {zipDownloading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Preparazione ZIP...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Scarica ZIP
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && filteredPhotos.length > 0 && (
        <ClientLightbox 
          photos={filteredPhotos}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrevious={goToPrevious}
          onNext={goToNext}
          canFavorite={gallery?.can_favorite}
          favorites={content.favorites}
          onToggleFavorite={toggleFavorite}
        />
      )}
    </div>
  );
};

// Client Photo Card Component
const ClientPhotoCard = ({ photo, onOpenLightbox, canFavorite, isFavorite, onToggleFavorite, selectionMode, isSelected, onToggleSelect }) => {
  const displayUrl = photo.thumbnail_url || photo.url;

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    onToggleFavorite();
  };

  return (
    <div 
      className={`group relative rounded-xl overflow-hidden bg-gray-100 cursor-pointer ${
        selectionMode && isSelected ? 'ring-4 ring-black' : ''
      }`}
      onClick={selectionMode ? onToggleSelect : onOpenLightbox}
    >
      <img 
        src={displayUrl} 
        alt={photo.original_filename}
        className={`w-full h-auto block transition-opacity ${selectionMode && isSelected ? 'opacity-80' : ''}`}
        loading="lazy"
      />
      
      {/* Selection indicator */}
      {selectionMode && (
        <div className={`absolute top-2 left-2 z-20 w-6 h-6 rounded-full flex items-center justify-center border-2 transition-colors ${
          isSelected ? 'bg-black border-black' : 'bg-white/80 border-gray-400'
        }`}>
          {isSelected && <Check className="w-4 h-4 text-white" />}
        </div>
      )}
      
      {/* Favorite icon - small red heart without background when favorited */}
      {!selectionMode && canFavorite && isFavorite && (
        <button 
          onClick={handleFavoriteClick}
          className="absolute top-2 right-2 z-20 transition-all hover:scale-110"
        >
          <Heart className="w-4 h-4 fill-red-500 text-red-500 drop-shadow-md" />
        </button>
      )}
      
      {/* Favorite button on hover - only when not favorited */}
      {!selectionMode && canFavorite && !isFavorite && (
        <button 
          onClick={handleFavoriteClick}
          className="absolute top-2 right-2 z-20 p-2 rounded-full transition-all bg-white/90 opacity-0 group-hover:opacity-100 hover:bg-white hover:scale-110"
        >
          <Heart className="w-5 h-5 text-gray-600" />
        </button>
      )}
      
      {/* Hover overlay - for zoom icon */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors pointer-events-none">
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
            <Image className="w-6 h-6 text-gray-700" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Client Video Card Component - Elegant full width style
const ClientVideoCard = ({ video }) => {
  return (
    <div className="w-full rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-b from-gray-900 to-black">
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <video 
          src={video.url}
          className="absolute inset-0 w-full h-full object-cover"
          controls
          preload="metadata"
          poster=""
        />
      </div>
    </div>
  );
};

// Client Lightbox Component
const ClientLightbox = ({ photos, currentIndex, onClose, onPrevious, onNext, canFavorite, favorites, onToggleFavorite }) => {
  const currentPhoto = photos[currentIndex];
  // Use medium_url for lightbox (faster loading), fallback to url, then thumbnail
  const displayUrl = currentPhoto?.medium_url || currentPhoto?.url || currentPhoto?.thumbnail_url;
  const isFavorite = favorites?.includes(currentPhoto?.id);
  const [isDownloading, setIsDownloading] = React.useState(false);

  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrevious();
      if (e.key === 'ArrowRight') onNext();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrevious, onNext]);

  const handleDownload = async () => {
    if (isDownloading) return;
    
    setIsDownloading(true);
    try {
      // Get direct S3 download URL from backend
      const response = await axios.get(`${API}/gallery/download/${currentPhoto.id}`);
      const { download_url, filename } = response.data;
      
      // Check if on mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile) {
        // For mobile: open URL in new tab (will trigger download or preview)
        window.open(download_url, '_blank');
      } else {
        // For desktop: use hidden link to trigger download
        const link = document.createElement('a');
        link.href = download_url;
        link.download = filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Errore durante il download');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    onToggleFavorite(currentPhoto.id);
  };

  if (!currentPhoto) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors z-10"
      >
        <X className="w-8 h-8" />
      </button>

      {/* Photo counter */}
      <div className="absolute top-4 left-4 text-white/70 text-sm">
        {currentIndex + 1} / {photos.length}
      </div>

      {/* Action buttons - responsive layout */}
      <div className="absolute bottom-16 md:bottom-4 left-1/2 transform -translate-x-1/2 flex flex-col md:flex-row items-center gap-2 md:gap-4 px-4 w-full md:w-auto">
        <span className="text-white/70 text-xs md:text-sm truncate max-w-[200px]">{currentPhoto.original_filename}</span>
        
        <div className="flex items-center gap-2">
          {canFavorite && (
            <button
              onClick={handleFavoriteClick}
              className={`p-2 rounded-lg transition-all flex items-center gap-1 md:gap-2 ${
                isFavorite 
                  ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                  : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
              }`}
            >
              <Heart className={`w-4 h-4 md:w-5 md:h-5 ${isFavorite ? 'fill-red-400' : ''}`} />
              <span className="text-xs md:text-sm">{isFavorite ? 'Preferito' : 'Aggiungi'}</span>
            </button>
          )}
          
          <button
            onClick={(e) => { e.stopPropagation(); handleDownload(); }}
            disabled={isDownloading}
            className={`p-2 rounded-lg transition-all flex items-center gap-1 md:gap-2 ${
              isDownloading 
                ? 'bg-blue-500/30 text-blue-300 cursor-wait' 
                : 'text-white/70 hover:text-white bg-white/10 hover:bg-white/20'
            }`}
          >
            {isDownloading ? (
              <>
                <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                <span className="text-xs md:text-sm">Scaricamento...</span>
              </>
            ) : (
              <>
                <Download className="w-4 h-4 md:w-5 md:h-5" />
                <span className="text-xs md:text-sm">Scarica</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Previous button */}
      {photos.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrevious(); }}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 text-white/70 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}

      {/* Image */}
      <div 
        className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={displayUrl}
          alt={currentPhoto.original_filename}
          className="max-w-full max-h-[85vh] object-contain"
        />
      </div>

      {/* Next button */}
      {photos.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 text-white/70 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}
    </div>
  );
};

export default ClientGalleryView;
